// Pure formatter: GameEvent -> { text, tone, weight? }. Kept separate from the
// React component so the event copy can grow without bloating GameOver.tsx.
//
// `tone` lets the timeline colorize lines without repeating logic in CSS:
//   'kill' (red), 'save' (green), 'info' (silver), 'mafia' (red),
//   'solo' (toxic), 'town' (silver), 'vote'.
// `weight` is optional emphasis — 'major' marks the dramatic beats (kills,
//   lynch votes, solo wins) so GameOver can render them bolder than the
//   passive scouting/manipulation lines.

import type { GameEvent, Lang, RoleConfig } from '../../../shared/src/types.js';

export interface EventLineCtx {
  t: Lang;
  nameById: Map<string, string>;
  roleById: Record<string, RoleConfig>;
}

export interface EventLine {
  text: string;
  tone: 'kill' | 'save' | 'info' | 'mafia' | 'solo' | 'town' | 'vote';
  weight?: 'major';
}

const KILL_METHOD_RU: Record<string, string> = {
  mafia:     'Мафия',
  maniac:    'Маньяк',
  arsonist:  'Поджигатель',
  vigilante: 'Линчеватель',
  alert:     'Ветеран',
  bodyguard: 'Телохранитель',
  werewolf:  'Оборотень',
  vote:      'Голосование',
};
const KILL_METHOD_UK: Record<string, string> = {
  mafia:     'Мафія',
  maniac:    'Маніяк',
  arsonist:  'Підпалювач',
  vigilante: 'Лінчувач',
  alert:     'Ветеран',
  bodyguard: 'Охоронець',
  werewolf:  'Перевертень',
  vote:      'Голосування',
};

function methodLabel(method: string, t: Lang): string {
  return (t === 'ru' ? KILL_METHOD_RU : KILL_METHOD_UK)[method] ?? method;
}

function name(ctx: EventLineCtx, id: string | null | undefined): string {
  if (!id) return '?';
  return ctx.nameById.get(id) ?? id.slice(-4);
}

function names(ctx: EventLineCtx, ids: string[]): string {
  return ids.length ? ids.map((id) => name(ctx, id)).join(', ') : '—';
}

function roleTitle(ctx: EventLineCtx, id: string | undefined): string {
  if (!id) return '?';
  const r = ctx.roleById[id];
  return r ? r.title[ctx.t] : id;
}

// Tiny language picker so each branch reads as one natural sentence per lang
// instead of stitching localized fragments.
function pick<T>(t: Lang, ru: T, uk: T): T {
  return t === 'ru' ? ru : uk;
}

export function eventLine(e: GameEvent, ctx: EventLineCtx): EventLine {
  const t = ctx.t;
  switch (e.kind) {
    case 'kill': {
      const who = name(ctx, e.victimId);
      const by = methodLabel(e.method, t);
      if (e.saved) {
        return {
          text: pick(t, `${by}: покушение на ${who} — спасён`, `${by}: замах на ${who} — врятований`),
          tone: 'save',
          weight: 'major',
        };
      }
      return {
        text: pick(t, `${by} убивает ${who}`, `${by} вбиває ${who}`),
        tone: 'kill',
        weight: 'major',
      };
    }
    case 'save':
      return {
        text:
          pick(t, `${name(ctx, e.saverId)} лечит ${name(ctx, e.targetId)}`, `${name(ctx, e.saverId)} лікує ${name(ctx, e.targetId)}`) +
          (e.succeeded ? pick(t, ' — спасение сработало', ' — порятунок спрацював') : ''),
        tone: 'save',
      };
    case 'protect':
      return {
        text:
          pick(t, `${name(ctx, e.bodyguardId)} прикрывает ${name(ctx, e.targetId)}`, `${name(ctx, e.bodyguardId)} прикриває ${name(ctx, e.targetId)}`) +
          (e.bodyguardDied ? pick(t, ' и гибнет вместо цели', ' і гине замість цілі') : ''),
        tone: 'save',
        ...(e.bodyguardDied ? { weight: 'major' as const } : {}),
      };
    case 'vest':
      return {
        text: pick(t, `${name(ctx, e.actorId)} надевает жилет`, `${name(ctx, e.actorId)} надягає жилет`),
        tone: 'save',
      };
    case 'alert': {
      const v = name(ctx, e.actorId);
      if (e.killedVisitorIds.length === 0) {
        return {
          text: pick(t, `${v} стоит на чеку, но никто не пришёл`, `${v} стоїть напоготові, але ніхто не прийшов`),
          tone: 'info',
        };
      }
      return {
        text: pick(
          t,
          `${v} стоит на чеку и убивает: ${names(ctx, e.killedVisitorIds)}`,
          `${v} стоїть напоготові й вбиває: ${names(ctx, e.killedVisitorIds)}`,
        ),
        tone: 'kill',
        weight: 'major',
      };
    }
    case 'check':
      return {
        text: pick(
          t,
          `${name(ctx, e.actorId)} проверяет ${name(ctx, e.targetId)}: результат ${e.result.black ? 'ЧЁРНЫЙ' : 'красный'}`,
          `${name(ctx, e.actorId)} перевіряє ${name(ctx, e.targetId)}: результат ${e.result.black ? 'ЧОРНИЙ' : 'червоний'}`,
        ),
        tone: 'info',
      };
    case 'inspect':
      return {
        text: pick(
          t,
          `${name(ctx, e.actorId)} опознаёт роль ${name(ctx, e.targetId)}: ${roleTitle(ctx, e.result.roleId)}`,
          `${name(ctx, e.actorId)} розпізнає роль ${name(ctx, e.targetId)}: ${roleTitle(ctx, e.result.roleId)}`,
        ),
        tone: 'info',
      };
    case 'investigate':
      return {
        text: pick(
          t,
          `${name(ctx, e.actorId)} расследует ${name(ctx, e.targetId)}: ${e.result.isKiller ? 'может убивать' : 'не убийца'}`,
          `${name(ctx, e.actorId)} розслідує ${name(ctx, e.targetId)}: ${e.result.isKiller ? 'може вбивати' : 'не вбивця'}`,
        ),
        tone: 'info',
      };
    case 'watch':
      return {
        text: pick(
          t,
          `${name(ctx, e.actorId)} следит за ${name(ctx, e.targetId)}: ${e.visitorIds.length ? `приходили ${names(ctx, e.visitorIds)}` : 'никто не приходил'}`,
          `${name(ctx, e.actorId)} стежить за ${name(ctx, e.targetId)}: ${e.visitorIds.length ? `приходили ${names(ctx, e.visitorIds)}` : 'ніхто не приходив'}`,
        ),
        tone: 'info',
      };
    case 'track':
      return {
        text: pick(
          t,
          `${name(ctx, e.actorId)} ведёт ${name(ctx, e.targetId)}: ${e.visitedIds.length ? `ходил к ${names(ctx, e.visitedIds)}` : 'никуда не ходил'}`,
          `${name(ctx, e.actorId)} веде ${name(ctx, e.targetId)}: ${e.visitedIds.length ? `ходив до ${names(ctx, e.visitedIds)}` : 'нікуди не ходив'}`,
        ),
        tone: 'info',
      };
    case 'spy':
      return {
        text: pick(
          t,
          `${name(ctx, e.actorId)} держит хвост за ${name(ctx, e.targetId)}: пришли ${e.incoming.length}, ушёл ${e.outgoing.length}`,
          `${name(ctx, e.actorId)} тримає хвіст за ${name(ctx, e.targetId)}: прийшли ${e.incoming.length}, пішов ${e.outgoing.length}`,
        ),
        tone: 'info',
      };
    case 'distract':
      return {
        text: pick(t, `${name(ctx, e.actorId)} отвлекает ${name(ctx, e.targetId)}`, `${name(ctx, e.actorId)} відволікає ${name(ctx, e.targetId)}`),
        tone: 'info',
      };
    case 'silence':
      return {
        text: pick(t, `${name(ctx, e.actorId)} заставляет молчать ${name(ctx, e.targetId)}`, `${name(ctx, e.actorId)} змушує мовчати ${name(ctx, e.targetId)}`),
        tone: 'info',
      };
    case 'jail':
      return {
        text: pick(t, `${name(ctx, e.actorId)} сажает в карцер ${name(ctx, e.targetId)}`, `${name(ctx, e.actorId)} садить до карцеру ${name(ctx, e.targetId)}`),
        tone: 'info',
      };
    case 'frame':
      return {
        text: pick(t, `${name(ctx, e.actorId)} подставляет ${name(ctx, e.targetId)}`, `${name(ctx, e.actorId)} підставляє ${name(ctx, e.targetId)}`),
        tone: 'info',
      };
    case 'douse':
      return {
        text: pick(t, `${name(ctx, e.actorId)} обливает ${name(ctx, e.targetId)} бензином`, `${name(ctx, e.actorId)} обливає ${name(ctx, e.targetId)} бензином`),
        tone: 'info',
      };
    case 'taunt':
      return {
        text: pick(t, `${name(ctx, e.actorId)} навлекает на себя подозрения`, `${name(ctx, e.actorId)} накликає на себе підозри`),
        tone: 'solo',
      };
    case 'redirect':
      return {
        text: pick(
          t,
          `${name(ctx, e.actorId)} меняет цели местами: ${name(ctx, e.aId)} и ${name(ctx, e.bId)}`,
          `${name(ctx, e.actorId)} міняє цілі місцями: ${name(ctx, e.aId)} і ${name(ctx, e.bId)}`,
        ),
        tone: 'info',
      };
    case 'ignite':
      return {
        text: pick(
          t,
          `${name(ctx, e.actorId)} поджигает облитых — сгорают: ${names(ctx, e.victimIds)}`,
          `${name(ctx, e.actorId)} підпалює облитих — згорають: ${names(ctx, e.victimIds)}`,
        ),
        tone: 'kill',
        weight: 'major',
      };
    case 'rampage':
      return {
        text: pick(
          t,
          `${name(ctx, e.actorId)} буянит у ${name(ctx, e.targetId)} — гибнут гости: ${names(ctx, e.visitorVictimIds)}`,
          `${name(ctx, e.actorId)} лютує в ${name(ctx, e.targetId)} — гинуть гості: ${names(ctx, e.visitorVictimIds)}`,
        ),
        tone: 'kill',
        weight: 'major',
      };
    case 'plague':
      return {
        text:
          pick(t, `${name(ctx, e.actorId)} заражает ${name(ctx, e.targetId)}`, `${name(ctx, e.actorId)} заражає ${name(ctx, e.targetId)}`) +
          (e.spreadToIds.length
            ? pick(t, `; зараза расходится на ${names(ctx, e.spreadToIds)}`, `; зараза розходиться на ${names(ctx, e.spreadToIds)}`)
            : ''),
        tone: 'info',
      };
    case 'execute':
      return {
        text: pick(t, `${name(ctx, e.actorId)} застреливает ${name(ctx, e.targetId)} среди дня`, `${name(ctx, e.actorId)} застрелює ${name(ctx, e.targetId)} серед дня`),
        tone: 'kill',
        weight: 'major',
      };
    case 'shield':
      return {
        text:
          pick(t, `${name(ctx, e.actorId)} прикрывает ${name(ctx, e.targetId)} от голосования`, `${name(ctx, e.actorId)} прикриває ${name(ctx, e.targetId)} від голосування`) +
          (e.consumedByVote ? pick(t, ' — голос отбит', ' — голос відбито') : ''),
        tone: 'save',
      };
    case 'reveal':
      return {
        text: pick(t, `${name(ctx, e.actorId)} публично оглашает роль ${name(ctx, e.targetId)}`, `${name(ctx, e.actorId)} публічно оголошує роль ${name(ctx, e.targetId)}`),
        tone: 'info',
      };
    case 'vote': {
      if (e.eliminatedId) {
        return {
          text: pick(t, `Город голосует и вешает ${name(ctx, e.eliminatedId)}`, `Місто голосує і вішає ${name(ctx, e.eliminatedId)}`),
          tone: 'vote',
          weight: 'major',
        };
      }
      if (e.tied) {
        return {
          text: pick(t, 'Голосование закончилось ничьёй — никого не вешают', 'Голосування завершилося нічиєю — нікого не вішають'),
          tone: 'vote',
          weight: 'major',
        };
      }
      if (e.shielded) {
        return {
          text: pick(t, 'Адвокат отбивает голос — казни не было', 'Адвокат відбиває голос — страти не було'),
          tone: 'vote',
          weight: 'major',
        };
      }
      return {
        text: pick(t, 'Город воздержался — никого не повесили', 'Місто утрималося — нікого не повісили'),
        tone: 'vote',
        weight: 'major',
      };
    }
    case 'jester-win':
      return {
        text: pick(t, `${name(ctx, e.jesterId)} — Шут — добивается своей казни и побеждает!`, `${name(ctx, e.jesterId)} — Блазень — домагається своєї страти й перемагає!`),
        tone: 'solo',
        weight: 'major',
      };
    case 'executioner-win':
      return {
        text: pick(
          t,
          `${name(ctx, e.executionerId)} — Палач — добивается казни ${name(ctx, e.targetId)} и побеждает!`,
          `${name(ctx, e.executionerId)} — Кат — домагається страти ${name(ctx, e.targetId)} й перемагає!`,
        ),
        tone: 'solo',
        weight: 'major',
      };
  }
}
